import type { Canvas, FabricObject } from 'fabric';
import type { PrintContext } from '@/types/printContext';
import { SYSTEM_LAYER_NAMES } from '@/types/canvas';

export interface PrintAreaBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Resolve the print area bounds shown on the canvas.
 * Prefers the visible print-area border so alignment matches the blue dashed box.
 */
export function getCanvasPrintAreaBounds(
  canvas: Canvas
): PrintAreaBounds | null {
  const border = canvas
    .getObjects()
    .find((obj) => obj.name === SYSTEM_LAYER_NAMES.PRINT_AREA_BORDER);

  if (border) {
    return {
      x: border.left ?? 0,
      y: border.top ?? 0,
      width: (border.width ?? 0) * (border.scaleX ?? 1),
      height: (border.height ?? 0) * (border.scaleY ?? 1),
    };
  }

  const userObject = canvas
    .getObjects()
    .find((obj) => !obj.name?.startsWith('__'));

  const clipPath = userObject?.clipPath as FabricObject | undefined;
  if (clipPath) {
    return {
      x: clipPath.left ?? 0,
      y: clipPath.top ?? 0,
      width: (clipPath.width ?? 0) * (clipPath.scaleX ?? 1),
      height: (clipPath.height ?? 0) * (clipPath.scaleY ?? 1),
    };
  }

  return null;
}

/**
 * Centre of the print area on the canvas: the visible border first, else
 * the active print context. Null without either: there is no default area
 * to centre on.
 */
export function getPrintAreaCenter(
  canvas: Canvas,
  printContext: PrintContext | null
): { x: number; y: number } | null {
  const bounds = getCanvasPrintAreaBounds(canvas);

  if (bounds) {
    return {
      x: bounds.x + bounds.width / 2,
      y: bounds.y + bounds.height / 2,
    };
  }

  if (!printContext) return null;
  return {
    x: printContext.offsetX + printContext.widthPx / 2,
    y: printContext.offsetY + printContext.heightPx / 2,
  };
}
